'use client';

import {
  AlertCircle,
  AlertTriangle,
  Download,
  ExternalLink,
  Calendar,
  Clock,
  Monitor,
  Smartphone,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { formatDate, formatTime, getNumberFormatter } from '@/lib/audit/pro-report-utils';
import type { SummaryData, SeverityDataItem } from './types';
import type { ReportTabView } from '@/components/audit/ReportTabs';
import styles from '@/components/audit/ProReportResults.module.css';

interface VisualHeroProps {
  screenshotUrl: string;
  url: string;
  timestamp: string;
  summary: SummaryData;
  total: number;
  severityData: SeverityDataItem[];
  deviceView: ReportTabView;
  locale?: string;
  onDownloadPDF: () => void;
}

export function VisualHero({
  screenshotUrl,
  url,
  timestamp,
  summary,
  total,
  severityData,
  deviceView,
  locale = 'en',
  onDownloadPDF,
}: VisualHeroProps) {
  const fmt = getNumberFormatter(locale);

  return (
    <section className={styles.visualHero}>
      {/* Screenshot Preview */}
      <div className={styles.heroScreenshot}>
        <img
          src={screenshotUrl}
          alt={`Screenshot of ${url}`}
          className={styles.heroScreenshotImg}
        />
        <div className={styles.heroScreenshotOverlay} />

        {/* Device Label */}
        <span className={`${styles.heroDeviceLabel} ${deviceView === 'mobile' ? styles.mobile : ''}`}>
          {deviceView === 'mobile' ? (
            <>
              <Smartphone size={12} /> Mobile
            </>
          ) : (
            <>
              <Monitor size={12} /> Desktop
            </>
          )}
        </span>

        {/* Stats Overlay */}
        <div className={styles.heroScreenshotStats}>
          {summary.criticalIssues > 0 && (
            <span className={`${styles.heroStatBadge} ${styles.critical}`}>
              <AlertCircle size={12} />
              {fmt.format(summary.criticalIssues)} Critical
            </span>
          )}
          {summary.highIssues > 0 && (
            <span className={`${styles.heroStatBadge} ${styles.high}`}>
              <AlertTriangle size={12} />
              {fmt.format(summary.highIssues)} High
            </span>
          )}
          <span className={`${styles.heroStatBadge} ${styles.total}`}>
            {fmt.format(total)} Issues Found
          </span>
        </div>
      </div>

      {/* Summary Column */}
      <div className={styles.heroSummary}>
        <div>
          <h1 className={styles.heroTitle}>Your UX Audit Report</h1>
          <a href={url} target="_blank" rel="noopener noreferrer" className={styles.heroUrl}>
            <span className={styles.heroUrlText}>{url}</span>
            <ExternalLink size={14} />
          </a>
        </div>

        {/* Score Display */}
        <div className={styles.heroScore}>
          <div className={styles.heroScoreCircle}>
            <svg viewBox="0 0 100 100" className={styles.heroScoreSvg}>
              <circle
                cx="50"
                cy="50"
                r="40"
                fill="none"
                stroke="var(--audit-border-subtle)"
                strokeWidth="8"
              />
              {severityData.map((item, index) => {
                const offset = severityData.slice(0, index).reduce((acc, s) => acc + s.count, 0);
                const percentage = (item.count / total) * 100;
                const circumference = 2 * Math.PI * 40;
                const strokeDasharray = `${(percentage / 100) * circumference} ${circumference}`;
                const rotation = (offset / total) * 360 - 90;
                if (item.count === 0) return null;
                return (
                  <circle
                    key={item.label}
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke={item.color}
                    strokeWidth="8"
                    strokeDasharray={strokeDasharray}
                    strokeLinecap="round"
                    transform={`rotate(${rotation} 50 50)`}
                  />
                );
              })}
            </svg>
            <div className={styles.heroScoreCenter}>
              <span className={styles.heroScoreNumber}>{fmt.format(total)}</span>
              <span className={styles.heroScoreLabel}>Issues</span>
            </div>
          </div>

          <div className={styles.heroSeverityList}>
            {severityData.map((item) => (
              <div key={item.label} className={styles.heroSeverityItem}>
                <span className={`${styles.heroSeverityDot} ${styles[item.label.toLowerCase()]}`} />
                <span className={styles.heroSeverityLabel}>{item.label}</span>
                <span className={styles.heroSeverityCount}>{fmt.format(item.count)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Meta Info */}
        <div className={styles.heroMeta}>
          <span className={styles.heroMetaItem}>
            <Calendar size={14} />
            {formatDate(timestamp, locale)}
          </span>
          <span className={styles.heroMetaItem}>
            <Clock size={14} />
            {formatTime(timestamp, locale)}
          </span>
        </div>

        {/* Actions */}
        <div className={styles.heroActions}>
          <Button onClick={onDownloadPDF} className={styles.heroPrimaryBtn}>
            <Download size={16} />
            Download PDF
          </Button>
        </div>
      </div>
    </section>
  );
}
